import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { IntlProvider } from 'react-intl';

import { getConfig } from '@edx/frontend-platform';
import { getLocale } from '@edx/frontend-platform/i18n';
import {
  Form,
  Spinner,
  StatefulButton } from '@openedx/paragon';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Cookies from 'universal-cookie';

import { resetPassword, validateToken } from './data/actions';
import {
  FORM_SUBMISSION_ERROR, PASSWORD_RESET_ERROR, PASSWORD_VALIDATION_ERROR, TOKEN_STATE } from './data/constants';
import { resetPasswordResultSelector } from './data/selectors';
import { validatePassword } from './data/service';
import messages from './messages';
import ResetPasswordFailure from './ResetPasswordFailure';
import { PasswordField } from '../common-components';
import {
  LETTER_REGEX, LOGIN_PAGE, NUMBER_REGEX, RESET_PAGE } from '../data/constants';
import { getAllPossibleQueryParams, updatePathWithQueryParams, windowScrollTo } from '../data/utils';

import authBG from '../assets/authBG.jpg';
import logo from '../assets/logo.png';
/* eslint-disable-next-line global-require */
const arMessages = require("../translations/ar.js");
import { useT } from '../context/LanguageContext';


const ResetPasswordPage = (props) => {
  const { t, toggleLang, isArabic } = useT();
  const newPasswordError = t('password.validation.message');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [errorCode, setErrorCode] = useState(null);
  const { token: tokenParam } = useParams();
  const firstHyphen = tokenParam ? tokenParam.indexOf('-') : -1;
  const uid = firstHyphen > -1 ? tokenParam.substring(0, firstHyphen) : null;
  const token = firstHyphen > -1 ? tokenParam.substring(firstHyphen + 1) : null;
  const navigate = useNavigate();

  // Locale handling for KSU design
  const getInitialLocale = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlLocale = urlParams.get('lang');
    if (urlLocale && ['ar', 'en'].includes(urlLocale)) {
      return urlLocale;
    }

    const cookies = new Cookies();
    const cookieName = getConfig().LANGUAGE_PREFERENCE_COOKIE_NAME || 'openedx-language-preference';
    const cookieLocale = cookies.get(cookieName);
    if (cookieLocale && ['ar', 'en'].includes(cookieLocale)) {
      return cookieLocale;
    }

    const localStorageLocale = localStorage.getItem(cookieName);
    if (localStorageLocale && ['ar', 'en'].includes(localStorageLocale)) {
      return localStorageLocale;
    }

    return getLocale() || 'en';
  };

  const [currentLocale, setCurrentLocale] = useState(getInitialLocale());
  const isRtl = currentLocale === 'ar';

  useEffect(() => {
    if (isRtl) {
      document.documentElement.setAttribute('dir', 'rtl');
      document.documentElement.setAttribute('lang', 'ar');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
      document.documentElement.setAttribute('lang', 'en');
    }
  }, [isRtl]);

  useEffect(() => {
    if (props.status !== TOKEN_STATE.PENDING && props.status !== PASSWORD_RESET_ERROR) {
      setErrorCode(props.status);
    }
    if (props.status === PASSWORD_VALIDATION_ERROR) {
      setFormErrors({ newPassword: newPasswordError });
    }
  }, [props.status, newPasswordError]);

  const validatePasswordFromBackend = async (password) => {
    let errorMessage = '';
    try {
      const payload = {
        reset_password_page: true,
        password };
      errorMessage = await validatePassword(payload);
    } catch (err) {
      errorMessage = '';
    }
    setFormErrors({ ...formErrors, newPassword: errorMessage });
  };

  const validateInput = (name, value) => {
    switch (name) {
      case 'newPassword':
        if (!value || !LETTER_REGEX.test(value) || !NUMBER_REGEX.test(value) || value.length < 8) {
          formErrors.newPassword = t('password.validation.message');
        } else {
          validatePasswordFromBackend(value);
        }
        break;
      case 'confirmPassword':
        if (!value) {
          formErrors.confirmPassword = t('confirm.your.password');
        } else if (value !== newPassword) {
          formErrors.confirmPassword = t('passwords.do.not.match');
        } else {
          formErrors.confirmPassword = '';
        }
        break;
      default:
        break;
    }
    setFormErrors({ ...formErrors });
    return !Object.values(formErrors).some(x => (x !== ''));
  };

  const handleOnBlur = (event) => {
    const { name, value } = event.target;
    validateInput(name, value);
  };

  const handleConfirmPasswordChange = (e) => {
    const { value } = e.target;

    setConfirmPassword(value);
    validateInput('confirmPassword', value);
  };

  const handleOnFocus = (e) => {
    setFormErrors({ ...formErrors, [e.target.name]: '' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const isPasswordValid = validateInput('newPassword', newPassword);
    const isPasswordConfirmed = validateInput('confirmPassword', confirmPassword);

    if (isPasswordValid && isPasswordConfirmed) {
      const formPayload = {
        new_password1: newPassword,
        new_password2: confirmPassword };
      const params = getAllPossibleQueryParams();
      props.resetPassword(formPayload, props.uid, props.token, params);
    } else {
      setErrorCode(FORM_SUBMISSION_ERROR);
      windowScrollTo({ left: 0, top: 0, behavior: 'smooth' });
    }
  };

  if (props.status === TOKEN_STATE.PENDING) {
    if (uid && token) {
      props.validateToken(uid, token);
      return (
        <div
          className="ksu-login-page"
          style={{
            backgroundImage: `url(${authBG})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            justifyContent: 'center' }}
        >
          <Spinner animation="border" variant="light" className="spinner--position-centered" />
        </div>
      );
    }
  } else if (props.status === PASSWORD_RESET_ERROR) {
    navigate(updatePathWithQueryParams(RESET_PAGE));
  } else if (props.status === 'success') {
    navigate(updatePathWithQueryParams(LOGIN_PAGE));
  } else {
    return (
      <>
        <Helmet>
          <title>
            {t('reset.password.page.title', { siteName: getConfig().SITE_NAME })}
          </title>
        </Helmet>

        <div
          className="ksu-login-page"
          dir={isRtl ? 'rtl' : 'ltr'}
          style={{
            backgroundImage: `url(${authBG})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat' }}
        >
          {/* Language Switcher */}
          <div className="ksu-lang-switcher">
            <button type="button" className="lang-btn" onClick={toggleLang}>
              {isArabic ? 'EN' : 'العربية'}
            </button>
          </div>

          {/* Header with Logo */}
          <header className="ksu-header">
            <div className="ksu-logo-container">
              <a href="/">
                <img
                  src={logo}
                  alt="Platform Logo"
                  className="ksu-logo"
                />
              </a>
            </div>
          </header>

          {/* Main Content Card */}
          <main className="ksu-main-content">
            <div className="ksu-login-card">
              <h1 className="ksu-login-title">
                {t('reset.password')}
              </h1>

              <div className="ksu-register-link">
                <Link to={updatePathWithQueryParams(LOGIN_PAGE)}>
                  {t('sign.in')}
                </Link>
              </div>

              <ResetPasswordFailure errorCode={errorCode} errorMsg={props.errorMsg} />

              <p className="mb-4" style={{ textAlign: 'start' }}>
                {t('reset.password.page.instructions')}
              </p>

              <Form id="set-reset-password-form" name="set-reset-password-form" className="ksu-form">
                <div className="ksu-form-group">
                  <PasswordField
                    name="newPassword"
                    value={newPassword}
                    handleChange={(e) => setNewPassword(e.target.value)}
                    handleBlur={handleOnBlur}
                    handleFocus={handleOnFocus}
                    errorMessage={formErrors.newPassword}
                    floatingLabel={t('new.password.label')}
                  />
                </div>
                <div className="ksu-form-group">
                  <PasswordField
                    name="confirmPassword"
                    value={confirmPassword}
                    handleChange={handleConfirmPasswordChange}
                    handleFocus={handleOnFocus}
                    errorMessage={formErrors.confirmPassword}
                    showRequirements={false}
                    floatingLabel={t('confirm.password.label')}
                  />
                </div>
                <StatefulButton
                  id="submit-new-password"
                  name="submit-new-password"
                  type="submit"
                  variant="brand"
                  className="ksu-login-btn"
                  state={props.status}
                  labels={{
                    default: t('reset.password'),
                    pending: '' }}
                  onClick={e => handleSubmit(e)}
                  onMouseDown={(e) => e.preventDefault()}
                />
              </Form>
            </div>
          </main>
        </div>
      </>
    );
  }
  return null;
};

ResetPasswordPage.defaultProps = {
  status: null,
  uid: null,
  token: null,
  errorMsg: null };

ResetPasswordPage.propTypes = {
  resetPassword: PropTypes.func.isRequired,
  validateToken: PropTypes.func.isRequired,
  uid: PropTypes.string,
  token: PropTypes.string,
  status: PropTypes.string,
  errorMsg: PropTypes.string };

const ConnectedResetPasswordPage = connect(
  resetPasswordResultSelector,
  {
    resetPassword,
    validateToken },
)(ResetPasswordPage);

// Wrapper component with IntlProvider for Arabic locale
const ResetPasswordPageWrapper = (props) => {
  const cookies = new Cookies();
  const cookieName = getConfig().LANGUAGE_PREFERENCE_COOKIE_NAME || 'openedx-language-preference';

  // Get locale from URL, cookie, or localStorage and sync storage
  const getLocaleValue = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlLocale = urlParams.get('lang');

    // If URL has locale, use it and sync to storage
    if (urlLocale && ['ar', 'en'].includes(urlLocale)) {
      // Sync URL locale to cookie and localStorage
      const hostname = window.location.hostname;
      const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
      const cookieOptions = {
        path: '/',
        maxAge: 31536000,
        sameSite: 'lax' };
      if (!isLocalhost) {
        const domainParts = hostname.split('.');
        if (domainParts.length >= 2) {
          cookieOptions.domain = `.${domainParts.slice(-2).join('.')}`;
        }
        if (window.location.protocol === 'https:') {
          cookieOptions.secure = true;
        }
      }
      cookies.set(cookieName, urlLocale, cookieOptions);
      localStorage.setItem(cookieName, urlLocale);
      return urlLocale;
    }

    // Then check cookie
    const cookieLocale = cookies.get(cookieName);
    if (cookieLocale && ['ar', 'en'].includes(cookieLocale)) {
      return cookieLocale;
    }

    // Then localStorage
    const localStorageLocale = localStorage.getItem(cookieName);
    if (localStorageLocale && ['ar', 'en'].includes(localStorageLocale)) {
      return localStorageLocale;
    }

    return 'en';
  };

  const locale = getLocaleValue();

  if (locale === 'ar') {
    return (
      <IntlProvider locale="ar" messages={arMessages}>
        <ConnectedResetPasswordPage {...props} />
      </IntlProvider>
    );
  }

  return <ConnectedResetPasswordPage {...props} />;
};

export default ResetPasswordPageWrapper;
